// AssignmentFilters.jsx
import React, { memo } from 'react';
import {
    Box,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    Chip,
    ListItemText,
    TextField,
    Button,
    Typography,
} from '@mui/material';

const renderMultiValue = (selected) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
            <Chip key={value} label={value} size="small" />
        ))}
    </Box>
);

const AssignmentFilters = ({
    view,
    company,
    companies,
    location,
    locations,
    user,
    users,
    lawArea,
    lawAreas,
    roles,
    roleOptions,
    periodicities,
    periodicityOptions,
    category,
    activity,
    actOrRule,
    startDueDate,
    endDueDate,
    includeClosed,
    onCompanyChange,
    onLocationChange,
    onUserChange,
    onLawAreaChange,
    onRolesChange,
    onPeriodicitiesChange,
    onCategoryChange,
    onActivityChange,
    onActOrRuleChange,
    onStartDueDateChange,
    onEndDueDateChange,
    onIncludeClosedChange,
    onSubmit,
    onClear,
}) => {
    return (
        <Paper
            sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: 2,
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: 600 }}
                color="text.secondary"
            >
                Filters
            </Typography>

            <Grid container spacing={2}>
                {/* Company Selection */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Company</InputLabel>
                        <Select
                            value={company}
                            onChange={onCompanyChange}
                            label="Company"
                        >
                            {companies.map((comp, index) => (
                                <MenuItem key={index} value={comp}>
                                    {comp}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Location Multi-Select */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Location</InputLabel>
                        <Select
                            multiple
                            value={location}
                            onChange={onLocationChange}
                            input={<OutlinedInput label="Location" />}
                            renderValue={renderMultiValue}
                        >
                            {locations.map((loc) => (
                                <MenuItem key={loc} value={loc}>
                                    <Checkbox
                                        checked={
                                            location.indexOf(loc) > -1
                                        }
                                    />
                                    <ListItemText primary={loc} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* User Multi-Select (filter) */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>User</InputLabel>
                        <Select
                            multiple
                            value={user}
                            onChange={onUserChange}
                            input={<OutlinedInput label="User" />}
                            renderValue={renderMultiValue}
                        >
                            {/* "Unassigned" removed as requested */}
                            {users.map((usr) => (
                                <MenuItem key={usr} value={usr}>
                                    <Checkbox
                                        checked={
                                            user.indexOf(usr) > -1
                                        }
                                    />
                                    <ListItemText primary={usr} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Advanced Fields */}
                {view === 'Advanced' && (
                    <>
                        {/* Law Area Multi-Select with "All" */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Law Area</InputLabel>
                                <Select
                                    multiple
                                    value={lawArea}
                                    onChange={onLawAreaChange}
                                    input={
                                        <OutlinedInput label="Law Area" />
                                    }
                                    renderValue={renderMultiValue}
                                >
                                    <MenuItem value="__ALL__">
                                        <Checkbox
                                            checked={
                                                lawAreas.length > 0 &&
                                                lawArea.length ===
                                                lawAreas.length
                                            }
                                            indeterminate={
                                                lawArea.length > 0 &&
                                                lawArea.length <
                                                lawAreas.length
                                            }
                                        />
                                        <ListItemText primary="All" />
                                    </MenuItem>

                                    {lawAreas.map((area) => (
                                        <MenuItem
                                            key={area}
                                            value={area}
                                        >
                                            <Checkbox
                                                checked={
                                                    lawArea.indexOf(
                                                        area
                                                    ) > -1
                                                }
                                            />
                                            <ListItemText
                                                primary={area}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Category */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Category"
                                fullWidth
                                size="small"
                                value={category}
                                onChange={onCategoryChange}
                            />
                        </Grid>

                        {/* Activity */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Activity"
                                fullWidth
                                size="small"
                                value={activity}
                                onChange={onActivityChange}
                            />
                        </Grid>

                        {/* Act or Rule */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Act or Rule"
                                fullWidth
                                size="small"
                                value={actOrRule}
                                onChange={onActOrRuleChange}
                            />
                        </Grid>

                        {/* Start Due Date */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Start Due Date"
                                type="date"
                                fullWidth
                                size="small"
                                value={startDueDate}
                                onChange={onStartDueDateChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* End Due Date */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="End Due Date"
                                type="date"
                                fullWidth
                                size="small"
                                value={endDueDate}
                                onChange={onEndDueDateChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Periodicity Multi-Select */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Periodicity</InputLabel>
                                <Select
                                    multiple
                                    value={periodicities}
                                    onChange={onPeriodicitiesChange}
                                    input={
                                        <OutlinedInput label="Periodicity" />
                                    }
                                    renderValue={renderMultiValue}
                                >
                                    {periodicityOptions.map((p) => (
                                        <MenuItem key={p} value={p}>
                                            <Checkbox
                                                checked={
                                                    periodicities.indexOf(
                                                        p
                                                    ) > -1
                                                }
                                            />
                                            <ListItemText primary={p} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Role Multi-Select */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Role</InputLabel>
                                <Select
                                    multiple
                                    value={roles}
                                    onChange={onRolesChange}
                                    input={
                                        <OutlinedInput label="Role" />
                                    }
                                    renderValue={renderMultiValue}
                                >
                                    {roleOptions.map((r) => (
                                        <MenuItem key={r} value={r}>
                                            <Checkbox
                                                checked={
                                                    roles.indexOf(r) >
                                                    -1
                                                }
                                            />
                                            <ListItemText primary={r} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Include Closed Trackers */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={includeClosed}
                                    onChange={onIncludeClosedChange}
                                />
                                <Typography variant="body2">
                                    Include Closed Trackers
                                </Typography>
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>

            {/* Buttons */}
            <Box
                sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                }}
            >
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={onSubmit}
                >
                    Submit
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={onClear}
                >
                    Clear
                </Button>
            </Box>
        </Paper>
    );
};

export default memo(AssignmentFilters);
